import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, ClipboardCheck, RotateCcw, Loader2, AlertTriangle, Archive, Flame } from "lucide-react";
import { HistoryDeltaHeadline, CategoryDelta, useHistory, buildPrevCategoryMap } from "@/components/HistoryDelta";
import RedFlagSummary from "@/components/RedFlagSummary";

const COLOR_PILL = {
  red:         { bg: "bg-warn-soft",      text: "text-warn",  border: "border-warn/40" },
  orange:      { bg: "bg-warn-soft",      text: "text-warn",  border: "border-warn/40" },
  amber:       { bg: "bg-warn-soft",      text: "text-warn",  border: "border-warn/40" },
  light_green: { bg: "bg-good-soft",      text: "text-good",  border: "border-good/40" },
  dark_green:  { bg: "bg-good-soft",      text: "text-good",  border: "border-good/40" },
};
const COLOR_BAR = {
  red: "bg-warn", orange: "bg-warn", amber: "bg-warn",
  light_green: "bg-good", dark_green: "bg-good",
};
const PILL_FOR = (c) => COLOR_PILL[c] || COLOR_PILL.amber;
const BAR_FOR = (c) => COLOR_BAR[c] || "bg-brand";

const ENDPOINTS = {
  schema:  () => `/pcei/schema`,
  get:     (t) => `/tenders/${t}/pcei`,
  start:   (t) => `/tenders/${t}/pcei/start`,
  answers: (t) => `/tenders/${t}/pcei/answers`,
  submit:  (t) => `/tenders/${t}/pcei/submit`,
  retake:  (t) => `/tenders/${t}/pcei/retake`,
};

/**
 * PCEI — Project Complexity & Exposure Index. Risk-direction; higher score = worse.
 * Mirrors BNBD lifecycle (gate → quiz → results → retake) with 40 questions and
 * decimal scoring. Scores remain hidden during the quiz.
 */
export default function PceiModule({ tenderId }) {
  const [schema, setSchema] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [archivedCount, setArchivedCount] = useState(0);
  const [view, setView] = useState("loading");
  const [qIdx, setQIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showRetakeModal, setShowRetakeModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: sch }, { data: latest }] = await Promise.all([
          api.get(ENDPOINTS.schema()),
          api.get(ENDPOINTS.get(tenderId)),
        ]);
        setSchema(sch);
        setArchivedCount(latest.archived_count || 0);
        if (latest.assessment) {
          setAssessment(latest.assessment);
          setView(latest.assessment.status === "completed" ? "results" : "gate");
        } else {
          setView("gate");
        }
      } catch (ex) {
        toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      }
    })();
  }, [tenderId]);

  const answers = assessment?.answers || {};
  const currentQ = schema?.questions?.[qIdx];
  const totalQ = schema?.questions?.length || 40;
  const currentCategoryQs = useMemo(() => {
    if (!schema || !currentQ) return [];
    return schema.questions.filter((q) => q.category === currentQ.category);
  }, [schema, currentQ]);
  const currentCategoryLabel = useMemo(() => {
    if (!schema || !currentQ) return "";
    return schema.categories.find((c) => c.id === currentQ.category)?.label || "";
  }, [schema, currentQ]);
  const currentCategoryQNumber = useMemo(() => {
    if (!currentCategoryQs.length || !currentQ) return 1;
    return currentCategoryQs.findIndex((q) => q.code === currentQ.code) + 1;
  }, [currentCategoryQs, currentQ]);

  const selectAnswer = useCallback(async (code, optionIdx) => {
    if (!assessment) return;
    const next = { ...(assessment.answers || {}), [code]: optionIdx };
    setAssessment({ ...assessment, answers: next });
    setSaving(true);
    try {
      const { data } = await api.patch(ENDPOINTS.answers(tenderId), { answers: { [code]: optionIdx } });
      setAssessment(data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setSaving(false); }
  }, [assessment, tenderId]);

  const goNext = () => { if (qIdx < totalQ - 1) setQIdx(qIdx + 1); };
  const goPrev = () => { if (qIdx > 0) setQIdx(qIdx - 1); };

  const startAttempt = async () => {
    try {
      const { data } = await api.post(ENDPOINTS.start(tenderId));
      setAssessment(data);
      const firstUnanswered = (schema.questions || []).findIndex((q) => !(q.code in (data.answers || {})));
      setQIdx(firstUnanswered === -1 ? 0 : firstUnanswered);
      setView("quiz");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(ENDPOINTS.submit(tenderId), { answers: {} });
      setAssessment(data);
      setView("results");
      toast.success(`PCEI complete · ${data.total_score}/100 · ${data.tier_label}`);
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      if (detail?.code === "incomplete" && Array.isArray(detail.missing_questions)) {
        toast.error(`Please answer all 40 questions. Missing: ${detail.missing_questions.slice(0, 5).join(", ")}${detail.missing_questions.length > 5 ? "…" : ""}`);
        const idx = (schema.questions || []).findIndex((q) => detail.missing_questions.includes(q.code));
        if (idx >= 0) setQIdx(idx);
      } else {
        toast.error(formatApiErrorDetail(detail) || ex.message);
      }
    } finally { setSubmitting(false); }
  };

  const retakeConfirmed = async () => {
    setShowRetakeModal(false);
    try {
      const { data } = await api.post(ENDPOINTS.retake(tenderId));
      setAssessment(data);
      setArchivedCount((c) => c + 1);
      setQIdx(0);
      setView("quiz");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  if (view === "loading") {
    return <div className="text-sm text-ink-muted uppercase tracking-wider" data-testid="pcei-loading">Loading PCEI…</div>;
  }

  if (view === "gate") {
    const hasCompleted = assessment?.status === "completed";
    const hasDraft = assessment?.status === "in_progress";
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-up" data-testid="pcei-gate">
        <div className="lg:col-span-2 card-flat p-7">
          <div className="stat-label">PCEI · Risk Exposure</div>
          <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Project Complexity & Exposure Index</h2>
          <p className="text-sm text-ink-muted mt-2 leading-relaxed max-w-prose">
            40 single-select questions across 4 risk dimensions. Estimated 18–25 minutes. Higher scores indicate higher
            risk. Your answers auto-save — you can pause and resume.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            {schema?.categories?.map((c) => {
              const qcount = (schema.questions || []).filter((q) => q.category === c.id).length;
              return (
                <div key={c.id} className="border border-ink-line p-3 rounded-sm bg-white">
                  <div className="stat-label">{c.label}</div>
                  <div className="font-num text-xs text-ink-muted mt-1">{qcount} questions · max {c.weight} pts</div>
                </div>
              );
            })}
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            {hasDraft && (
              <button
                onClick={() => { const i = (schema?.questions || []).findIndex((q) => !(q.code in (assessment.answers || {}))); setQIdx(i === -1 ? 0 : i); setView("quiz"); }}
                data-testid="pcei-resume-btn"
                className="px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors"
              >
                Resume <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {!hasDraft && !hasCompleted && (
              <button onClick={startAttempt} data-testid="pcei-start-btn"
                className="px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors">
                <ClipboardCheck className="w-4 h-4" /> Start assessment
              </button>
            )}
            {hasCompleted && (
              <>
                <button onClick={() => setView("results")} data-testid="pcei-view-result-btn"
                  className="px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors">
                  View latest result <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => setShowRetakeModal(true)} data-testid="pcei-retake-btn"
                  className="px-4 py-2.5 text-sm border border-ink-line hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors">
                  <RotateCcw className="w-4 h-4" /> Retake
                </button>
              </>
            )}
          </div>
          {hasDraft && (
            <div className="mt-4 text-xs text-ink-muted">
              Draft in progress — {Object.keys(assessment.answers || {}).length} of {totalQ} answered.
            </div>
          )}
        </div>
        <div className="card-flat p-6 flex flex-col">
          <div className="stat-label">How it works</div>
          <ol className="text-sm space-y-2 mt-3 list-decimal pl-4 text-ink-muted">
            <li>Pick one of five options per question.</li>
            <li>Scores are hidden — option 1 is best, option 5 is worst.</li>
            <li>On submit, you'll see a risk tier (Low → Critical), category breakdown, and Risk Hotspots with mitigation tips.</li>
            <li>Result is attached to this tender.</li>
          </ol>
          {archivedCount > 0 && (
            <div className="mt-6 pt-4 border-t border-ink-line text-xs text-ink-muted flex items-center gap-2" data-testid="pcei-archived-count">
              <Archive className="w-3 h-3" /> {archivedCount} archived attempt{archivedCount === 1 ? "" : "s"}
            </div>
          )}
        </div>
        {showRetakeModal && <RetakeModal previousScore={assessment?.total_score} previousTier={assessment?.tier_label}
          onConfirm={retakeConfirmed} onClose={() => setShowRetakeModal(false)} />}
      </div>
    );
  }

  if (view === "quiz" && currentQ) {
    const answeredCount = Object.keys(answers).length;
    const selectedIdx = answers[currentQ.code];
    const allAnswered = answeredCount === totalQ;
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-up" data-testid="pcei-quiz">
        <div className="lg:col-span-2 card-flat p-7">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">
              {currentCategoryLabel} · {currentQ.code} of {currentQ.category.charAt(0).toUpperCase() + currentCategoryQs[currentCategoryQs.length - 1].code.slice(1)}
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-num text-ink-muted" data-testid="pcei-progress-text">
              Q{qIdx + 1} / {totalQ}
            </div>
          </div>
          <div className="h-1 bg-secondary rounded-sm overflow-hidden">
            <div className="h-full bg-brand transition-all" style={{ width: `${((qIdx + 1) / totalQ) * 100}%` }} data-testid="pcei-progress-bar" />
          </div>

          <h3 className="font-display text-xl font-bold tracking-tight mt-6" data-testid="pcei-q-label">
            {currentQ.label}
          </h3>

          <div className="mt-5 space-y-2.5">
            {currentQ.options.map((o, i) => {
              const isSel = selectedIdx === i;
              return (
                <button
                  key={`${currentQ.code}-${i}`}
                  type="button"
                  onClick={() => selectAnswer(currentQ.code, i)}
                  data-testid={`pcei-${currentQ.code}-opt-${i}`}
                  className={`w-full flex items-center gap-3 text-left px-4 py-3 text-sm rounded-sm border transition-colors ${
                    isSel
                      ? "border-brand bg-brand/5 text-ink"
                      : "border-ink-line bg-white hover:bg-secondary text-ink-muted hover:text-ink"
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                    isSel ? "border-brand bg-brand text-white" : "border-ink-line"
                  }`}>
                    {isSel && <Check className="w-3 h-3" />}
                  </span>
                  <span>{o.label}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-7 pt-5 border-t border-ink-line flex items-center justify-between flex-wrap gap-3">
            <button onClick={goPrev} disabled={qIdx === 0} data-testid="pcei-back-btn"
              className="px-4 py-2 text-sm border border-ink-line hover:bg-secondary rounded-sm flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="text-xs text-ink-muted">
              {saving && <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}
              {!saving && selectedIdx === undefined && <span>Pick an option to continue.</span>}
              {!saving && selectedIdx !== undefined && <span>Saved.</span>}
            </div>
            {qIdx < totalQ - 1 ? (
              <button onClick={goNext} disabled={selectedIdx === undefined} data-testid="pcei-next-btn"
                className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-sm flex items-center gap-1.5 transition-colors">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={!allAnswered || submitting} data-testid="pcei-submit-btn"
                className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-sm flex items-center gap-1.5 transition-colors">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit <Check className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>

        <div className="card-flat p-6">
          <div className="stat-label">Progress</div>
          <div className="font-num text-5xl font-bold mt-2 text-brand">
            {answeredCount}<span className="text-ink-muted">/{totalQ}</span>
          </div>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted mt-1">Answered</div>
          <div className="mt-5 grid grid-cols-8 gap-1.5" data-testid="pcei-dots">
            {schema.questions.map((q, i) => {
              const answered = q.code in answers;
              const isCurrent = i === qIdx;
              return (
                <button key={q.code} onClick={() => setQIdx(i)}
                  className={`aspect-square text-[9px] font-num rounded-sm transition-all ${
                    isCurrent ? "bg-brand text-white ring-2 ring-brand/30" :
                    answered ? "bg-good text-white hover:bg-good/80" :
                    "bg-secondary text-ink-muted hover:bg-ink-line"
                  }`}
                  data-testid={`pcei-dot-${q.code}`}
                >
                  {q.code}
                </button>
              );
            })}
          </div>
          <div className="mt-6 pt-5 border-t border-ink-line text-[11px] text-ink-muted leading-relaxed">
            Scores are hidden while you answer. The full risk breakdown appears on the results screen.
          </div>
        </div>
      </div>
    );
  }

  if (view === "results" && assessment?.status === "completed") {
    return (
      <ResultsScreen
        assessment={assessment}
        schema={schema}
        tenderId={tenderId}
        onRetake={() => setShowRetakeModal(true)}
        showRetakeModal={showRetakeModal}
        retakeConfirmed={retakeConfirmed}
        onCloseRetake={() => setShowRetakeModal(false)}
      />
    );
  }
  return null;
}

function ResultsScreen({ assessment, schema, onRetake, showRetakeModal, retakeConfirmed, onCloseRetake, tenderId }) {
  const a = assessment;
  const pill = PILL_FOR(a.tier_color);
  const historyUrl = `/tenders/${tenderId}/pcei/history`;
  const historyData = useHistory(historyUrl, a.id);
  const { previous, attempts } = historyData;
  const prevCatMap = useMemo(() => buildPrevCategoryMap(previous, "id"), [previous]);
  return (
    <div className="space-y-6 animate-fade-up" data-testid="pcei-results">
      <div className={`card-flat p-8 border-l-4 ${pill.border}`} data-testid="pcei-result-headline">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="stat-label flex items-center gap-3">
              PCEI risk exposure
              {attempts > 1 && <span className="chip border-ink-line bg-secondary text-ink-muted text-[10px] font-num" data-testid="pcei-attempts-chip">Attempt {attempts}</span>}
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <div className="font-num text-7xl font-bold leading-none">{a.total_score}</div>
              <div className="text-2xl font-num text-ink-muted">/ 100</div>
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <span className={`px-3 py-1 text-sm font-display font-bold rounded-sm border ${pill.bg} ${pill.text} ${pill.border}`} data-testid="pcei-tier-chip">
                {a.tier_label}
              </span>
            </div>
            {a.action && <p className="text-sm text-ink-muted mt-3 max-w-prose" data-testid="pcei-action">{a.action}</p>}
            <HistoryDeltaHeadline historyUrl={historyUrl} historyData={historyData} currentId={a.id} currentScore={a.total_score} direction="risk" testid="pcei-history-delta" />
          </div>
          <button onClick={onRetake} data-testid="pcei-result-retake-btn"
            className="px-4 py-2 text-sm border border-ink-line hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors">
            <RotateCcw className="w-4 h-4" /> Retake
          </button>
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="stat-label">Category breakdown</div>
        <div className="space-y-3 mt-4" data-testid="pcei-breakdown">
          {(a.category_breakdown || []).map((c) => {
            const flagPill = PILL_FOR(c.flag_color);
            const prev = prevCatMap[c.id];
            return (
              <div key={c.id} className="space-y-1" data-testid={`pcei-cat-${c.id}`}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{c.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-num text-ink-muted" data-testid={`pcei-cat-pct-${c.id}`}>{Math.round((c.score / c.max_score) * 100)}%</span>
                    {prev && <CategoryDelta prevScore={prev.score} prevMax={prev.max} curScore={c.score} curMax={c.max_score} direction="risk" testid={`pcei-cat-delta-${c.id}`} />}
                    <span className={`chip text-[10px] ${flagPill.bg} ${flagPill.text} ${flagPill.border}`}>{c.flag_label}</span>
                  </span>
                </div>
                <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                  <div className={`h-full transition-all ${BAR_FOR(c.flag_color)}`} style={{ width: `${Math.min(100, c.pct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {a.hotspots && a.hotspots.length > 0 && (
        <div className="card-flat p-6" data-testid="pcei-hotspots">
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-warn" />
            <div className="stat-label">Risk Hotspots · {a.hotspots.length} identified</div>
          </div>
          <div className="space-y-4 mt-4">
            {a.hotspots.map((h) => {
              const tone = h.score >= 2.5 ? "border-warn/40 bg-warn-soft" : "border-warn/20 bg-warn-soft/50";
              return (
                <div key={h.code} className={`p-4 rounded-sm border ${tone}`} data-testid={`pcei-hotspot-${h.code}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-display font-bold text-sm">{h.code} — {h.option}</div>
                    <div className="font-num text-sm font-bold text-warn">{h.score.toFixed(1)}</div>
                  </div>
                  <div className="text-xs text-ink-muted mt-1">{h.question}</div>
                  {h.mitigation && (
                    <div className="text-xs text-ink mt-3 italic leading-relaxed">
                      Mitigation: {h.mitigation}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="text-[11px] uppercase tracking-wider text-ink-muted">
        Completed {a.completed_at ? new Date(a.completed_at).toLocaleString() : "—"}
      </div>

      {/* Red flags — PCEI uses risk direction (worst = highest-point = highest-risk option) */}
      <RedFlagSummary questions={schema?.questions || []} answers={a.answers || {}} direction="risk"
        questionKeyFn={(q) => String(q.code)} testidPrefix="pcei-redflag" />

      {showRetakeModal && (
        <RetakeModal previousScore={a.total_score} previousTier={a.tier_label}
          onConfirm={retakeConfirmed} onClose={onCloseRetake} />
      )}
    </div>
  );
}

function RetakeModal({ previousScore, previousTier, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 bg-ink/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="pcei-retake-modal">
      <div className="bg-white max-w-md w-full border border-ink-line rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-ink-line">
          <div className="font-display font-bold tracking-tight">Retake PCEI assessment?</div>
        </div>
        <div className="p-6 text-sm space-y-3 text-ink-muted">
          <p>The current attempt will be archived and a fresh assessment will start.</p>
          {Number.isFinite(previousScore) && (
            <p>
              Previous result: <strong className="text-ink font-num">{previousScore} / 100</strong>
              {previousTier && <> · <strong className="text-ink">{previousTier}</strong></>}
            </p>
          )}
        </div>
        <div className="px-6 py-4 border-t border-ink-line flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-ink-line hover:bg-secondary rounded-sm transition-colors" data-testid="pcei-retake-cancel">Cancel</button>
          <button onClick={onConfirm} className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors" data-testid="pcei-retake-confirm">
            <RotateCcw className="w-4 h-4" /> Retake
          </button>
        </div>
      </div>
    </div>
  );
}
